import { useState } from 'react';
import { Box, Grid, Typography, Popover, Button, FormControlLabel, InputLabel, TextField, Checkbox } from '@mui/material';
import CreateIcon from '@mui/icons-material/Create';
import { Formik, Form, Field } from 'formik';

interface NumberComponentProps {
  values: any;
  index: number;
  childIndex: number;
  setFieldValue: (key: string, value: any) => void;
  onSubmit: (data: any) => void;
  handleChange: (e: any) => void;
  handleBlur: (e: any) => void;
}

export default function NumberComponent({
  values,
  index,
  setFieldValue,
  onSubmit,
  handleChange,
  handleBlur,
  childIndex
}: NumberComponentProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleEditClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'edit-number-popover' : undefined;

  const field = values.fields[index] || {};
  const settings = field.settings || {};

  const child = values.fields[index].children[childIndex];

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container alignItems="center" spacing={1}>
        <Grid item xs={12}>
          <InputLabel sx={{ color: 'black' }}>{child.question}</InputLabel>
        </Grid>

        <Grid item xs={11}>
          <TextField
            fullWidth
            type="number"
            name={`fields.${index}.answer`}
            value=""
            onChange={handleChange}
            onBlur={handleBlur}
            inputProps={{
              min: settings.min,
              max: settings.max,
              step: settings.step
            }}
            size="small"
          />
        </Grid>

        <Grid item xs={1}>
          <Box sx={{ cursor: 'pointer', color: 'primary.main' }} onClick={handleEditClick}>
            <CreateIcon fontSize="medium" />
          </Box>
        </Grid>
      </Grid>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{ sx: { width: 350, p: 2 } }}
      >
        <Formik
          initialValues={{
            question: child.question || '',
            description: child.description || '',
            min: child.settings?.min || '',
            max: child.settings?.max || '',
            regexp: child.settings?.regexp || '',
            required: child.settings?.required || false,
            isSupportFileRequired: child.settings?.isSupportFileRequired || false,
            noOfSupportImages: child.settings?.noOfSupportImages || ''
          }}
          onSubmit={(formValues) => {
            const updatedChild = {
              ...child,
              question: formValues.question,
              description: formValues.description,
              settings: {
                ...child.settings,
                min: formValues.min,
                max: formValues.max,
                regexp: formValues.regexp,
                required: formValues.required,
                isSupportFileRequired: formValues.isSupportFileRequired,
                noOfSupportImages: formValues.isSupportFileRequired ? formValues.noOfSupportImages : ''
              }
            };
            setFieldValue(`fields.${index}.children.${childIndex}`, updatedChild);
            handleClosePopover();
          }}
        >
          {({ values: formValues, handleChange, handleBlur, setFieldValue }) => (
            <Form>
              <Typography variant="subtitle1" mb={2}>
                Edit Field Settings
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <InputLabel>Label</InputLabel>
                </Grid>
                <Grid item xs={8}>
                  <Field as={TextField} name="question" fullWidth size="small" onChange={handleChange} onBlur={handleBlur} />
                </Grid>

                <Grid item xs={4}>
                  <InputLabel>Description</InputLabel>
                </Grid>
                <Grid item xs={8}>
                  <Field as={TextField} name="description" fullWidth size="small" onChange={handleChange} onBlur={handleBlur} />
                </Grid>

                <Grid item xs={4}>
                  <InputLabel>Min</InputLabel>
                </Grid>
                <Grid item xs={8}>
                  <Field as={TextField} name="min" type="number" fullWidth size="small" />
                </Grid>

                <Grid item xs={4}>
                  <InputLabel>Max</InputLabel>
                </Grid>
                <Grid item xs={8}>
                  <Field as={TextField} name="max" type="number" fullWidth size="small" />
                </Grid>

                <Grid item xs={4}>
                  <InputLabel>RegExp</InputLabel>
                </Grid>
                <Grid item xs={8}>
                  <Field as={TextField} name="regexp" type="text" fullWidth size="small" />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Checkbox checked={formValues.required} onChange={(e) => setFieldValue('required', e.target.checked)} />}
                    label="Required"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Checkbox name="isSupportFileRequired" checked={formValues.isSupportFileRequired} onChange={handleChange} />}
                    label="Support File Required"
                  />
                </Grid>

                {formValues.isSupportFileRequired && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="noOfSupportImages"
                      type="number"
                      label="Number of Images"
                      value={formValues.noOfSupportImages}
                      onChange={handleChange}
                      size="small"
                      inputProps={{ min: 1 }}
                      sx={{ mt: 1 }}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Box mt={2} display="flex" justifyContent="flex-end">
                    <Button type="submit" size="small" variant="contained">
                      Save
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Popover>
    </Box>
  );
}
